import React from "react";
import { Box, Card, CardActionArea, Typography, Chip, Button } from "@mui/material";
import { PlayCircleOutline } from "@mui/icons-material";
import { useSession } from "../context/SessionContext";
import { formatPrice, isOutOfStock, productCardImage, productPrice, productStrikePrice, stripHtml } from "../utils/helpers";

// The whole card, and the explicit "Add" button (matching denzo-waiter's own
// ProductCard), both just open the full product detail dialog — options/
// addons still need that dialog either way, so "Add" isn't a shortcut that
// skips it, only a more familiar, obvious tap target next to the price.
export default function ProductCard({ product, onPress }) {
  const { store, currency } = useSession();
  const price = productPrice(product);
  const strike = productStrikePrice(product);
  const imgSrc = productCardImage(store?.id, product.cover_image);
  const description = stripHtml(product.description);
  // Still opens the detail dialog (to see what it is), just can't be added.
  const outOfStock = isOutOfStock(product);

  return (
    <Card variant="outlined" sx={{ display: "flex", alignItems: "center", p: 1, mb: 1.25, borderRadius: 2, overflow: "hidden" }}>
      {/* minWidth:0 lets the name/description ellipsis instead of pushing
          the Add button off the card — see denzo-waiter's identical card. */}
      <CardActionArea
        onClick={() => onPress(product)}
        sx={{ display: "flex", alignItems: "center", gap: 1.5, borderRadius: 2, flex: 1, minWidth: 0 }}
      >
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Box
            component={imgSrc ? "img" : "div"}
            src={imgSrc || undefined}
            sx={{
              width: 72,
              height: 72,
              borderRadius: 1.5,
              objectFit: "cover",
              bgcolor: "grey.100",
              opacity: outOfStock ? 0.5 : 1,
            }}
          />
          {product.video_url && (
            <PlayCircleOutline
              sx={{ position: "absolute", inset: 0, m: "auto", color: "#fff", filter: "drop-shadow(0 0 3px rgba(0,0,0,0.6))" }}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {product.name}
          </Typography>
          {description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {description}
            </Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              {formatPrice(price, currency)}
            </Typography>
            {strike != null && (
              <Typography variant="caption" color="text.disabled" sx={{ textDecoration: "line-through" }}>
                {formatPrice(strike, currency)}
              </Typography>
            )}
            {product.offer_label && <Chip label={product.offer_label} size="small" color="success" sx={{ height: 18, fontSize: 10 }} />}
          </Box>
          {outOfStock && (
            <Chip label="Out of stock" size="small" sx={{ mt: 0.5, height: 18, fontSize: 10, bgcolor: "grey.900", color: "#fff" }} />
          )}
        </Box>
      </CardActionArea>
      {/* flexShrink:0 keeps the button a fixed width down the whole list,
          regardless of how long each row's name/description runs. */}
      <Button
        size="small"
        variant="outlined"
        disabled={outOfStock}
        onClick={() => onPress(product)}
        sx={{ minWidth: 64, ml: 1, flexShrink: 0 }}
      >
        Add
      </Button>
    </Card>
  );
}
